import type { ReactNode } from 'react';
import { Seo } from '@/components/seo';
import { HeroHeader } from '@/components/header';
import FooterSection from '@/components/footer';

type LegalDocumentProps = {
  title: string;
  description: string;
  path: string;
  heading: string;
  effectiveDate: string;
  children: ReactNode;
};

/**
 * Shared shell for long-form legal pages (Privacy Policy, Terms of Use).
 * Marketing typography registers: display h1/h2, body at text-foreground/65.
 */
export function LegalDocument({
  title,
  description,
  path,
  heading,
  effectiveDate,
  children,
}: LegalDocumentProps) {
  return (
    <>
      <Seo title={title} description={description} path={path} />
      <HeroHeader />

      <main className="pt-24 md:pt-36 pb-16">
        <article className="mx-auto max-w-3xl px-6">
          <header className="mb-10 border-b border-line-strong pb-8">
            <h1 className="type-wonk font-display text-4xl font-normal leading-[1.12] tracking-tight text-foreground sm:text-5xl">
              {heading}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground/65">
              Effective date: {effectiveDate}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-foreground/65">
              Boundr is operated by Yashna Labs LLC (Washington, USA).
            </p>
          </header>

          <div className="space-y-10">{children}</div>
        </article>
      </main>

      <FooterSection />
    </>
  );
}

type LegalSectionProps = {
  id: string;
  title: string;
  children: ReactNode;
};

export function LegalSection({ id, title, children }: LegalSectionProps) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="mb-3 font-display text-xl font-medium text-foreground">{title}</h2>
      <div className="space-y-3 leading-relaxed text-foreground/65">{children}</div>
    </section>
  );
}
